/**
 * Single source of truth for ARC request / campaign application statuses.
 *
 * Used by both the superadmin CRM (App.js) and author dashboard (AuthorReadersTab).
 * The Reader Portal also uses these values when creating and displaying ARC requests.
 *
 * "denied" was previously used in the CRM — it is now standardised to "declined"
 * to match the rest of the platform. Legacy "denied" values are treated as "declined"
 * everywhere via the normaliseStatus helper.
 */

export const ARC_STATUSES = ['pending', 'approved', 'declined', 'waitlisted'];

export const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined',
  waitlisted: 'Waitlisted',
};

export const STATUS_COLOURS = {
  pending:    { badge: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500', bg: 'border-amber-200 bg-amber-50/50' },
  approved:   { badge: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500', bg: 'border-green-200 bg-green-50/50' },
  declined:   { badge: 'bg-red-100 text-red-700 border-red-200',       dot: 'bg-red-500',   bg: 'border-red-200 bg-red-50/50' },
  waitlisted: { badge: 'bg-blue-100 text-blue-700 border-blue-200',    dot: 'bg-blue-500',  bg: 'border-blue-200 bg-blue-50/50' },
};

/** Normalise legacy status values (e.g. "denied" → "declined") */
export function normaliseStatus(status) {
  if (status === 'denied') return 'declined';
  if (status === 'waitlist') return 'waitlisted';
  if (ARC_STATUSES.includes(status)) return status;
  return 'pending';
}

/** Get badge CSS classes for a status value (handles legacy values) */
export function getArcStatusBadgeClass(status) {
  const s = normaliseStatus(status);
  return STATUS_COLOURS[s]?.badge || STATUS_COLOURS.pending.badge;
}

/** Get dot CSS class for a status value */
export function getArcStatusDotClass(status) {
  const s = normaliseStatus(status);
  return STATUS_COLOURS[s]?.dot || STATUS_COLOURS.pending.dot;
}

/** Get background CSS class for card borders (used in CRM ARC requests list) */
export function getArcStatusBgClass(status) {
  const s = normaliseStatus(status);
  return STATUS_COLOURS[s]?.bg || 'border-slate-200 bg-slate-50/50';
}

/** Get display label for a status value */
export function getArcStatusLabel(status) {
  const s = normaliseStatus(status);
  return STATUS_LABELS[s] || 'Pending';
}
